import React, { useState, useEffect, useCallback } from 'react';
import IconPicker from './IconPicker';

const WordBankAdmin = ({ API, adminToken, isDarkMode, showNotification, settings, setSettings, appSettings, setAppSettings, handleToggleSetting, badges, loadBadgesData, onSettingsUpdate }) => {
  const [wordBankAnalytics, setWordBankAnalytics] = useState(null);
  const [wordBankUsers, setWordBankUsers] = useState([]);
  const [wordBankUsersPagination, setWordBankUsersPagination] = useState({});
  const [wordBankUsersPage, setWordBankUsersPage] = useState(1);
  const [wordBankUserSearch, setWordBankUserSearch] = useState('');
  const [wordBankUserDetail, setWordBankUserDetail] = useState(null);
  const [wordBankSubTab, setWordBankSubTab] = useState('analytics');
  const [editingBadge, setEditingBadge] = useState(null);
  const [editBadgeData, setEditBadgeData] = useState({
    name: '', description: '', icon: '', badgeType: 'words_learned', threshold: 1
  });
  const [newBadge, setNewBadge] = useState({
    name: '', description: '', icon: '', badgeType: 'words_learned', threshold: 1
  });
  const [wordBankIconUrl, setWordBankIconUrl] = useState('');
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [availableIcons, setAvailableIcons] = useState([]);
  const [iconSearchQuery, setIconSearchQuery] = useState('');

  const authHeaders = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };
  const authHeadersForForm = { 'Authorization': `Bearer ${adminToken}` };

  const loadUsersData = useCallback(async () => {
    try {
      const wbParams = new URLSearchParams({ page: wordBankUsersPage.toString(), per_page: '20' });
      if (wordBankUserSearch.trim()) wbParams.append('search', wordBankUserSearch.trim());
      const wbUsersRes = await fetch(`${API}/admin/word-bank/users?${wbParams}`, { headers: authHeaders });
      if (wbUsersRes.ok) {
        const wbData = await wbUsersRes.json();
        setWordBankUsers(wbData.users || []);
        setWordBankUsersPagination(wbData.pagination || {});
      }
    } catch (e) { console.error('Failed to load word bank users:', e); }
  }, [API, adminToken, wordBankUsersPage, wordBankUserSearch]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const wbAnalyticsRes = await fetch(`${API}/admin/word-bank/analytics`, { headers: authHeaders });
        if (wbAnalyticsRes.ok) {
          setWordBankAnalytics(await wbAnalyticsRes.json());
        }
      } catch (e) { console.error('Failed to load word bank analytics:', e); }
      loadUsersData();
      loadBadgesData();
      // Load current icon
      try {
        const sRes = await fetch(`${API}/api/settings`);
        if (sRes.ok) {
          const sData = await sRes.json();
          if (sData.word_bank_icon_url) setWordBankIconUrl(sData.word_bank_icon_url);
        }
      } catch (e) {}
    };
    loadData();
  }, []);

  useEffect(() => {
    loadUsersData();
  }, [wordBankUsersPage]);

  return (
    <div className="space-y-6">
      <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
        📦 Word Bank Admin
      </h2>

      {/* Sub-tab navigation */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'analytics', label: 'Analytics', icon: '📊' },
          { id: 'users', label: 'Users', icon: '👥' },
          { id: 'settings', label: 'Settings & Badges', icon: '⚙️' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setWordBankSubTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              wordBankSubTab === tab.id
                ? 'bg-blue-600 text-white'
                : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Analytics Sub-tab */}
      {wordBankSubTab === 'analytics' && (
        <div className="space-y-6">
          {/* Main stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Users', value: wordBankAnalytics?.totalUsers || 0, icon: '👥', color: 'blue' },
              { label: 'Words Saved', value: wordBankAnalytics?.totalWordsSaved || 0, icon: '📝', color: 'green' },
              { label: 'Words Mastered', value: wordBankAnalytics?.totalMastered || 0, icon: '✅', color: 'purple' },
              { label: 'Saved Today', value: wordBankAnalytics?.wordsSavedToday || 0, icon: '📅', color: 'orange' }
            ].map((stat, i) => (
              <div key={i} className={`p-4 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
                <div className="text-2xl mb-1">{stat.icon}</div>
                <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{stat.value}</div>
                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Puzzle stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: 'Puzzles Completed', value: wordBankAnalytics?.puzzleStats?.totalPuzzlesCompleted || 0, icon: '🧩' },
              { label: 'Total Puzzle Points', value: wordBankAnalytics?.puzzleStats?.totalPuzzlePoints || 0, icon: '🏆' },
              { label: 'Users Playing Puzzles', value: wordBankAnalytics?.puzzleStats?.usersWithPuzzles || 0, icon: '🎮' }
            ].map((stat, i) => (
              <div key={i} className={`p-4 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
                <div className="flex items-center gap-3">
                  <div className="text-3xl">{stat.icon}</div>
                  <div>
                    <div className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{stat.value}</div>
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{stat.label}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Most Popular Words */}
          <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
            <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Most Popular Saved Words (Top 10)
            </h3>
            {wordBankAnalytics?.mostPopularWords?.length > 0 ? (
              <div className="space-y-2">
                {wordBankAnalytics.mostPopularWords.map((word, i) => (
                  <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-bold w-6 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>#{i + 1}</span>
                      <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{word._id}</span>
                    </div>
                    <span className={`text-sm px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                      {word.count} {word.count === 1 ? 'user' : 'users'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No saved words yet.</p>
            )}
          </div>

          {/* Badge Distribution */}
          <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
            <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Badge Distribution
            </h3>
            {wordBankAnalytics?.badgeDistribution?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {wordBankAnalytics.badgeDistribution.map((badge, i) => (
                  <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <span className="text-2xl">{badge.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{badge.name}</div>
                      <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {badge.badgeType === 'words_learned' ? 'Words' : badge.badgeType === 'puzzle_points' ? 'Puzzle' : 'Streak'} — threshold: {badge.threshold}
                      </div>
                    </div>
                    <span className={`text-sm font-bold px-2 py-0.5 rounded-full ${
                      badge.usersEarned > 0
                        ? isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-700'
                        : isDarkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {badge.usersEarned}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No badges defined yet.</p>
            )}
          </div>
        </div>
      )}

      {/* Users Sub-tab */}
      {wordBankSubTab === 'users' && (
        <div className="space-y-4">
          {wordBankUserDetail ? (
            /* User Detail View */
            <div className="space-y-4">
              <button
                onClick={() => setWordBankUserDetail(null)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                ← Back to list
              </button>

              {/* User header */}
              <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
                <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {wordBankUserDetail.user?.username}
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{wordBankUserDetail.user?.email}</p>

                {/* Progress stats */}
                {wordBankUserDetail.progress && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                    {[
                      { label: 'Words Learned', value: wordBankUserDetail.progress.wordsLearned || 0 },
                      { label: 'Words Mastered', value: wordBankUserDetail.progress.wordsMastered || 0 },
                      { label: 'Puzzle Points', value: wordBankUserDetail.progress.puzzlePoints || 0 },
                      { label: 'Current Streak', value: `${wordBankUserDetail.progress.currentStreak || 0} days` },
                      { label: 'Longest Streak', value: `${wordBankUserDetail.progress.longestStreak || 0} days` },
                      { label: 'Total Reviews', value: wordBankUserDetail.progress.totalReviews || 0 },
                      { label: 'Correct Reviews', value: wordBankUserDetail.progress.correctReviews || 0 },
                      { label: 'Level', value: wordBankUserDetail.progress.level || 'Beginner' }
                    ].map((stat, i) => (
                      <div key={i} className={`px-3 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{stat.value}</div>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{stat.label}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Earned badges */}
              {wordBankUserDetail.earnedBadges?.length > 0 && (
                <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
                  <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Earned Badges</h4>
                  <div className="flex flex-wrap gap-3">
                    {wordBankUserDetail.earnedBadges.map((badge, i) => (
                      <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${isDarkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-50 border border-yellow-200'}`}>
                        <span className="text-xl">{badge.icon}</span>
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>{badge.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Words list */}
              <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
                <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  Saved Words ({wordBankUserDetail.words?.length || 0})
                </h4>
                {wordBankUserDetail.words?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                          <th className="text-left py-2 px-3">English</th>
                          <th className="text-left py-2 px-3">Darija</th>
                          <th className="text-center py-2 px-3">Level</th>
                          <th className="text-center py-2 px-3">Correct</th>
                          <th className="text-center py-2 px-3">Incorrect</th>
                          <th className="text-left py-2 px-3">Next Review</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wordBankUserDetail.words.map((word, i) => (
                          <tr key={i} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                            <td className={`py-2 px-3 font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{word.sourceText}</td>
                            <td className={`py-2 px-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{word.translatedText}</td>
                            <td className="py-2 px-3 text-center">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                                word.level >= 5 ? 'bg-green-100 text-green-700' :
                                word.level >= 3 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>{word.level || 0}{word.level >= 5 ? ' ✓' : ''}</span>
                            </td>
                            <td className={`py-2 px-3 text-center ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{word.correctCount || 0}</td>
                            <td className={`py-2 px-3 text-center ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{word.incorrectCount || 0}</td>
                            <td className={`py-2 px-3 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {word.level >= 5 ? 'Mastered' : word.nextReviewDate ? new Date(word.nextReviewDate).toLocaleDateString() : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No words saved.</p>
                )}
              </div>
            </div>
          ) : (
            /* Users List View */
            <>
              {/* Search bar */}
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Search by username or email..."
                  value={wordBankUserSearch}
                  onChange={(e) => setWordBankUserSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setWordBankUsersPage(1);
                      loadUsersData();
                    }
                  }}
                  className={`flex-1 px-4 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-400'}`}
                />
                <button
                  onClick={() => { setWordBankUsersPage(1); loadUsersData(); }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  Search
                </button>
                {wordBankUserSearch && (
                  <button
                    onClick={() => { setWordBankUserSearch(''); setWordBankUsersPage(1); loadUsersData(); }}
                    className={`px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Users table */}
              <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <tr className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                        <th className="text-left py-3 px-4">User</th>
                        <th className="text-center py-3 px-4">Words</th>
                        <th className="text-center py-3 px-4">Mastered</th>
                        <th className="text-center py-3 px-4">Puzzle Pts</th>
                        <th className="text-center py-3 px-4">Streak</th>
                        <th className="text-center py-3 px-4">Badges</th>
                        <th className="text-left py-3 px-4">Last Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wordBankUsers.length > 0 ? wordBankUsers.map((u, i) => (
                        <tr
                          key={i}
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API}/admin/word-bank/users/${u.userId}`, { headers: authHeaders });
                              if (res.ok) setWordBankUserDetail(await res.json());
                            } catch (e) { console.error('Failed to load user detail:', e); }
                          }}
                          className={`border-t cursor-pointer transition-colors ${isDarkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50'}`}
                        >
                          <td className="py-3 px-4">
                            <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{u.username}</div>
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{u.email}</div>
                          </td>
                          <td className={`py-3 px-4 text-center font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{u.wordsSaved}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${u.wordsMastered > 0 ? 'bg-green-100 text-green-700' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {u.wordsMastered}
                            </span>
                          </td>
                          <td className={`py-3 px-4 text-center ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{u.puzzlePoints}</td>
                          <td className={`py-3 px-4 text-center ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{u.currentStreak}d</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${u.badgesEarned > 0 ? 'bg-purple-100 text-purple-700' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {u.badgesEarned}
                            </span>
                          </td>
                          <td className={`py-3 px-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {u.lastActivity ? new Date(u.lastActivity).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="7" className={`py-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {wordBankUserSearch ? 'No users found matching your search.' : 'No users have saved any words yet.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {wordBankUsersPagination.totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Page {wordBankUsersPagination.page} of {wordBankUsersPagination.totalPages} ({wordBankUsersPagination.total} users)
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setWordBankUsersPage(prev => Math.max(1, prev - 1)); }}
                      disabled={wordBankUsersPage <= 1}
                      className={`px-3 py-1.5 rounded-lg text-sm ${wordBankUsersPage <= 1 ? 'opacity-50 cursor-not-allowed' : ''} ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => { setWordBankUsersPage(prev => Math.min(wordBankUsersPagination.totalPages, prev + 1)); }}
                      disabled={wordBankUsersPage >= wordBankUsersPagination.totalPages}
                      className={`px-3 py-1.5 rounded-lg text-sm ${wordBankUsersPage >= wordBankUsersPagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''} ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Settings & Badges Sub-tab */}
      {wordBankSubTab === 'settings' && (
        <>
        <div className={`p-6 rounded-lg border ${
          isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Word Bank Settings
            </h4>
            <button
              onClick={async () => {
                const currentStatus = appSettings.progressTrackingEnabled !== undefined ? appSettings.progressTrackingEnabled : true;
                const newStatus = !currentStatus;
                setAppSettings(prev => ({...prev, progressTrackingEnabled: newStatus}));
                setSettings(prev => ({...prev, progressTrackingEnabled: newStatus}));

                try {
                  const response = await fetch(`${API}/admin/settings/toggle-progress`, {
                    method: 'POST',
                    headers: authHeaders
                  });
                  if (response.ok) {
                    const data = await response.json();
                    setAppSettings(prev => ({...prev, progressTrackingEnabled: data.progressTrackingEnabled}));
                    setSettings(prev => ({...prev, progressTrackingEnabled: data.progressTrackingEnabled}));
                    showNotification(data.message, 'success');
                  } else {
                    setAppSettings(prev => ({...prev, progressTrackingEnabled: !newStatus}));
                    setSettings(prev => ({...prev, progressTrackingEnabled: !newStatus}));
                    showNotification('Failed to toggle progress tracking', 'error');
                  }
                } catch (error) {
                  setAppSettings(prev => ({...prev, progressTrackingEnabled: !newStatus}));
                  setSettings(prev => ({...prev, progressTrackingEnabled: !newStatus}));
                  showNotification('Failed to toggle progress tracking', 'error');
                }
              }}
              className={`px-3 py-1.5 rounded-lg transition-all text-xs font-medium flex items-center gap-1.5 ${
                (appSettings.progressTrackingEnabled !== undefined ? appSettings.progressTrackingEnabled : true)
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-500 text-white hover:bg-gray-600'
              }`}
              title={(appSettings.progressTrackingEnabled !== undefined ? appSettings.progressTrackingEnabled : true) ? 'Word Bank visible to users' : 'Word Bank hidden from users'}
            >
              {(appSettings.progressTrackingEnabled !== undefined ? appSettings.progressTrackingEnabled : true) ? '👁️' : '🚫'} Pro-Tra
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  Enable Word Bank
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Show/hide Word Bank for saving words, puzzles, and badges
                </p>
              </div>
              <button
                onClick={() => {
                  handleToggleSetting('progressTrackingEnabled', !settings.progressTrackingEnabled);
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  settings.progressTrackingEnabled ? 'bg-green-600' : 'bg-gray-300'
                }`}
                title="Toggle progress tracking setting"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.progressTrackingEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {settings.progressTrackingEnabled
                ? '✓ Word Bank enabled. Users can save words, play puzzles, and earn badges.'
                : '✗ Word Bank disabled. Word Bank button won\'t appear on frontend.'}
            </p>

            {/* Max saved words */}
            <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}` }}>
              <div>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  Max Saved Words
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Limit how many words each user can save
                </p>
              </div>
              <input
                type="number"
                min="10"
                max="500"
                value={settings.maxSavedWords || 10}
                onChange={async (e) => {
                  const val = parseInt(e.target.value) || 10;
                  setSettings(prev => ({ ...prev, maxSavedWords: val }));
                  try {
                    await fetch(`${API}/admin/settings`, {
                      method: 'PUT',
                      headers: { ...authHeaders, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ maxSavedWords: val })
                    });
                  } catch (err) {
                    console.error('Failed to save maxSavedWords:', err);
                  }
                }}
                className={`w-20 px-2 py-1 rounded-lg text-center text-sm font-medium ${isDarkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white text-gray-800 border-gray-300'} border`}
              />
            </div>

            {/* Button Icon */}
            <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}` }}>
              <div>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  Button Icon
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Custom icon for the Word Bank button on the translation page
                </p>
              </div>
              <div className="flex items-center gap-2">
                {wordBankIconUrl && (
                  <img src={`${API.replace('/api', '')}${wordBankIconUrl}`} alt="icon" className="w-8 h-8 object-contain rounded" />
                )}
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API}/icons?t=${Date.now()}`, { headers: { 'Authorization': `Bearer ${adminToken}` }, cache: 'no-store' });
                      if (res.ok) { const d = await res.json(); setAvailableIcons(d.icons || []); }
                    } catch (e) {}
                    setIconSearchQuery('');
                    setIconPickerOpen(true);
                  }}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700"
                >
                  Pick Icon
                </button>
                {wordBankIconUrl && (
                  <button
                    onClick={async () => {
                      setWordBankIconUrl('');
                      try {
                        await fetch(`${API}/admin/settings`, {
                          method: 'PUT',
                          headers: authHeaders,
                          body: JSON.stringify({ word_bank_icon_url: '' })
                        });
                        showNotification('Icon cleared', 'success');
                        if (onSettingsUpdate) onSettingsUpdate();
                      } catch (e) {}
                    }}
                    className="px-2 py-1.5 bg-red-600 text-white rounded-lg text-xs hover:bg-red-700"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* Help Text */}
            <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}` }}>
              <p className={`font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Help Text (How to Play)
              </p>
              <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Shown in the gear panel "How to Play" tab. Leave empty for default.
              </p>
              <textarea
                value={settings.wordBankHelpText || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, wordBankHelpText: e.target.value }))}
                onBlur={async (e) => {
                  const val = e.target.value;
                  try {
                    await fetch(`${API}/admin/settings`, {
                      method: 'PUT',
                      headers: { ...authHeaders, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ wordBankHelpText: val })
                    });
                    showNotification('Help text saved', 'success');
                  } catch (err) {
                    console.error('Failed to save help text:', err);
                  }
                }}
                rows={6}
                className={`w-full px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white text-gray-800 border-gray-300'} border resize-y`}
                placeholder="Leave empty for default help text..."
              />
            </div>
          </div>
        </div>

        {/* Achievement Badges */}
        <div className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Achievement Badges ({badges.length})
              </h3>
              <button
                onClick={async () => {
                  const currentStatus = appSettings.badgesEnabled !== undefined ? appSettings.badgesEnabled : true;
                  const newStatus = !currentStatus;
                  setAppSettings(prev => ({...prev, badgesEnabled: newStatus}));
                  setSettings(prev => ({...prev, badgesEnabled: newStatus}));

                  try {
                    const response = await fetch(`${API}/admin/settings/toggle-badges`, {
                      method: 'POST',
                      headers: authHeaders
                    });
                    if (response.ok) {
                      const data = await response.json();
                      setAppSettings(prev => ({...prev, badgesEnabled: data.badgesEnabled}));
                      setSettings(prev => ({...prev, badgesEnabled: data.badgesEnabled}));
                      showNotification(data.message, 'success');
                    } else {
                      setAppSettings(prev => ({...prev, badgesEnabled: !newStatus}));
                      setSettings(prev => ({...prev, badgesEnabled: !newStatus}));
                      showNotification('Failed to toggle badges', 'error');
                    }
                  } catch (error) {
                    setAppSettings(prev => ({...prev, badgesEnabled: !newStatus}));
                    setSettings(prev => ({...prev, badgesEnabled: !newStatus}));
                    showNotification('Failed to toggle badges', 'error');
                  }
                }}
                className={`px-4 py-2 rounded-lg transition-all text-sm font-medium flex items-center gap-2 ${
                  (appSettings.badgesEnabled !== undefined ? appSettings.badgesEnabled : true)
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-500 text-white hover:bg-gray-600'
                }`}
                title={(appSettings.badgesEnabled !== undefined ? appSettings.badgesEnabled : true) ? 'Badges visible to users' : 'Badges hidden from users'}
              >
                {(appSettings.badgesEnabled !== undefined ? appSettings.badgesEnabled : true) ? '👁️ Visible' : '🚫 Hidden'}
              </button>
            </div>
            <button
              onClick={async () => {
                try {
                  const response = await fetch(`${API}/admin/badges/initialize`, {
                    method: 'POST',
                    headers: authHeaders
                  });
                  if (response.ok) {
                    showNotification('Default badges initialized!', 'success');
                    loadBadgesData();
                  }
                } catch (error) {
                  showNotification('Failed to initialize badges', 'error');
                }
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              Initialize Default Badges
            </button>
          </div>

          {/* Create New Badge */}
          <div className={`p-4 rounded-lg border ${
            isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
          }`}>
            <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Create New Badge
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input type="text" placeholder="Badge Name" value={newBadge.name}
                onChange={(e) => setNewBadge({...newBadge, name: e.target.value})}
                className={`px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
              />
              <input type="text" placeholder="Icon (emoji)" value={newBadge.icon}
                onChange={(e) => setNewBadge({...newBadge, icon: e.target.value})}
                className={`px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
              />
              <input type="text" placeholder="Description" value={newBadge.description}
                onChange={(e) => setNewBadge({...newBadge, description: e.target.value})}
                className={`px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
              />
              <select value={newBadge.badgeType}
                onChange={(e) => setNewBadge({...newBadge, badgeType: e.target.value})}
                className={`px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
              >
                <option value="words_learned">Words Learned</option>
                <option value="streak_days">Streak Days</option>
                <option value="puzzle_points">Puzzle Points</option>
              </select>
              <input type="number" placeholder="Threshold" value={newBadge.threshold}
                onChange={(e) => setNewBadge({...newBadge, threshold: parseInt(e.target.value)})}
                className={`px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
              />
              <button
                onClick={async () => {
                  try {
                    const formData = new FormData();
                    formData.append('name', newBadge.name);
                    formData.append('description', newBadge.description);
                    formData.append('icon', newBadge.icon);
                    formData.append('badgeType', newBadge.badgeType);
                    formData.append('threshold', newBadge.threshold);

                    const response = await fetch(`${API}/admin/badges`, {
                      method: 'POST',
                      headers: authHeadersForForm,
                      body: formData
                    });

                    if (response.ok) {
                      showNotification('Badge created successfully!', 'success');
                      setNewBadge({ name: '', description: '', icon: '', badgeType: 'words_learned', threshold: 1 });
                      loadBadgesData();
                    }
                  } catch (error) {
                    showNotification('Failed to create badge', 'error');
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Badge
              </button>
            </div>
          </div>

          {/* Badges List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {badges.map((badge) => (
              <div key={badge.id} className={`p-4 rounded-lg border ${
                isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
              }`}>
                {editingBadge?.id === badge.id ? (
                  <div className="space-y-3">
                    <input type="text" value={editBadgeData.name}
                      onChange={(e) => setEditBadgeData({...editBadgeData, name: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                    />
                    <input type="text" value={editBadgeData.icon}
                      onChange={(e) => setEditBadgeData({...editBadgeData, icon: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                    />
                    <input type="text" value={editBadgeData.description}
                      onChange={(e) => setEditBadgeData({...editBadgeData, description: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                    />
                    <select value={editBadgeData.badgeType}
                      onChange={(e) => setEditBadgeData({...editBadgeData, badgeType: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                    >
                      <option value="words_learned">Words Learned</option>
                      <option value="streak_days">Streak Days</option>
                      <option value="puzzle_points">Puzzle Points</option>
                    </select>
                    <input type="number" value={editBadgeData.threshold}
                      onChange={(e) => setEditBadgeData({...editBadgeData, threshold: parseInt(e.target.value)})}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          try {
                            const formData = new FormData();
                            formData.append('name', editBadgeData.name);
                            formData.append('description', editBadgeData.description);
                            formData.append('icon', editBadgeData.icon);
                            formData.append('badgeType', editBadgeData.badgeType);
                            formData.append('threshold', editBadgeData.threshold);

                            const response = await fetch(`${API}/admin/badges/${badge.id}`, {
                              method: 'PUT',
                              headers: authHeadersForForm,
                              body: formData
                            });

                            if (response.ok) {
                              showNotification('Badge updated successfully!', 'success');
                              setEditingBadge(null);
                              loadBadgesData();
                            }
                          } catch (error) {
                            showNotification('Failed to update badge', 'error');
                          }
                        }}
                        className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingBadge(null)}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm ${
                          isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
                        }`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="text-4xl mb-2">{badge.icon}</div>
                        <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {badge.name}
                        </h4>
                        <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {badge.description}
                        </p>
                        <p className={`text-xs mt-2 font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          Type: {badge.badgeType === 'words_learned' ? 'Words Learned' : badge.badgeType === 'puzzle_points' ? 'Puzzle Points' : 'Streak Days'}
                        </p>
                        <p className={`text-xs font-medium ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          Threshold: {badge.threshold}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingBadge(badge);
                          setEditBadgeData({
                            name: badge.name,
                            description: badge.description,
                            icon: badge.icon,
                            badgeType: badge.badgeType,
                            threshold: badge.threshold
                          });
                        }}
                        className="flex-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={async () => {
                          if (window.confirm('Delete this badge?')) {
                            try {
                              const response = await fetch(`${API}/admin/badges/${badge.id}`, {
                                method: 'DELETE',
                                headers: authHeaders
                              });
                              if (response.ok) {
                                showNotification('Badge deleted successfully!', 'success');
                                loadBadgesData();
                              }
                            } catch (error) {
                              showNotification('Failed to delete badge', 'error');
                            }
                          }
                        }}
                        className="flex-1 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {badges.length === 0 && (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <p>No badges yet. Click "Initialize Default Badges" to create default badges.</p>
            </div>
          )}
        </div>
        </>
      )}
      {/* Icon Picker Modal — uses shared IconPicker (browses ALL Image Manager folders) */}
      {iconPickerOpen && (
        <IconPicker
          API={API}
          adminToken={adminToken}
          isDarkMode={isDarkMode}
          onClose={() => setIconPickerOpen(false)}
          onPick={async (url) => {
            setWordBankIconUrl(url);
            setIconPickerOpen(false);
            try {
              await fetch(`${API}/admin/settings`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify({ word_bank_icon_url: url })
              });
              showNotification('Word Bank icon saved', 'success');
              if (onSettingsUpdate) onSettingsUpdate();
            } catch (e) {}
          }}
        />
      )}
    </div>
  );
};

export default WordBankAdmin;
